import React from 'react';
import { FiExternalLink as ExternalLink, FiCreditCard as CreditCard } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { currentPolicies } from '../utils/policyStatus';
import { AGENCY_PHONE_DISPLAY, AGENCY_PHONE_TEL } from '../constants/contact';

const PaymentView = () => {
  const { user } = useAuth();

  const carrierPaymentPortals = [
    {
      name: 'Mercury Insurance',
      url: 'https://www.mercuryinsurance.com/payment',
      color: 'from-blue-500 to-blue-600'
    },
    {
      name: 'Progressive',
      url: 'https://www.progressive.com/payment',
      color: 'from-blue-600 to-blue-700'
    },
    {
      name: 'Travelers',
      url: 'https://www.travelers.com/payment',
      color: 'from-red-500 to-red-600'
    },
    {
      name: 'Nationwide',
      url: 'https://www.nationwide.com/payment',
      color: 'from-blue-700 to-blue-800'
    }
  ];

  const getUserCarriers = () => {
    // Current coverage only -- offering to take a payment toward coverage that
    // has already ended is worse than showing nothing.
    const userCarriers = [...new Set(currentPolicies(user?.policies).map(p => p.vendor))];
    return carrierPaymentPortals.filter(carrier => 
      userCarriers.some(userCarrier => 
        userCarrier.toLowerCase().includes(carrier.name.toLowerCase().split(' ')[0])
      )
    );
  };

  const getPoliciesForCarrier = (carrierName) => {
    return currentPolicies(user?.policies).filter(p => 
      p.vendor.toLowerCase().includes(carrierName.toLowerCase().split(' ')[0])
    ) || [];
  };

  const userCarriers = getUserCarriers();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Make a Payment</h1>
        <p className="text-gray-600">
          Select your insurance carrier to proceed to their secure payment portal
        </p>
      </div>

      {/* User's Carrier Payment Cards */}
      {userCarriers.length > 0 && (
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Your Insurance Carriers</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {userCarriers.map((carrier) => {
              const policies = getPoliciesForCarrier(carrier.name);

              return (
                <div 
                  key={carrier.name}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-200 card-hover"
                >
                  {/* Carrier Header */}
                  <div className={`bg-gradient-to-r ${carrier.color} p-6 text-white`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-xl font-bold">{carrier.name}</h3>
                        <p className="text-white/80">Secure Payment Portal</p>
                      </div>
                      <div className="w-16 h-10 bg-white/20 rounded flex items-center justify-center text-xs font-medium">
                        LOGO
                      </div>
                    </div>
                  </div>

                  {/* Policy Info */}
                  <div className="p-6">
                    <div className="space-y-3 mb-6">
                      {policies.map((policy) => (
                        <div key={policy.id} className="flex justify-between items-center">
                          <div>
                            <div className="font-medium text-gray-900">{policy.policyType}</div>
                            <div className="text-sm text-gray-600">Policy: {policy.policyNumber}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <a 
                      href={carrier.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-[#DC143C] text-white py-3 px-4 rounded-lg font-semibold hover:bg-[#B01030] transition-colors flex items-center justify-center gap-2"
                    >
                      <CreditCard size={20} /> Pay Now <ExternalLink size={16} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Available Carriers */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-4">All Payment Portals</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {carrierPaymentPortals.map((carrier) => (
            <div 
              key={carrier.name}
              className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-16 h-10 bg-gray-200 rounded flex items-center justify-center text-xs font-medium text-gray-600">
                  LOGO
                </div>
                <span className="text-sm text-gray-500">Secure Portal</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{carrier.name}</h3>
              <p className="text-gray-600 mb-4 text-sm">
                Pay your {carrier.name} policy online with their secure payment system
              </p>
              <a 
                href={carrier.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full border border-[#0038A8] text-[#0038A8] py-2 px-4 rounded-lg font-semibold hover:bg-[#0038A8] hover:text-white transition-colors flex items-center justify-center gap-2 text-sm"
              >
                Visit Portal <ExternalLink size={16} />
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Other Options */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Other Payment Options</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Phone Payment</h4>
            <p className="text-gray-600 text-sm mb-3">
              Call us directly to make a payment over the phone
            </p>
            <a 
              href={AGENCY_PHONE_TEL}
              className="inline-flex items-center gap-2 text-[#0038A8] font-semibold hover:underline"
            >
              <CreditCard size={16} /> {AGENCY_PHONE_DISPLAY}
            </a>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Mail Payment</h4>
            <p className="text-gray-600 text-sm mb-3">
              Send a check or money order to your carrier's payment address
            </p>
            <span className="text-gray-500 text-sm">
              Check your policy documents for mailing address
            </span>
          </div>
        </div>
      </div>

      {/* Security Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 mb-3">Security Notice</h3>
        <div className="space-y-2 text-blue-800 text-sm">
          <p>• All payment portals use bank-level encryption to protect your information</p>
          <p>• Never share your login credentials with anyone</p>
          <p>• Contact us immediately if you notice any suspicious activity</p>
          <p>• Keep records of all payments made online</p>
        </div>
      </div>
    </div>
  );
};

export default PaymentView;